// This file contains interfaces that reference multiple legacy fields with old naming conventions.
/* eslint-disable @typescript-eslint/naming-convention */
import * as _ from 'lodash';

import { Action, ResourceType } from '../../typings/permissions';
import { isFreeTier } from '../subscriptions';
import type { Permission } from '../../typings/permissions';
import type { OrganizationAccessLevel } from '../model-typings';
import type { StripePriceLimit } from '../model-typings/stripepricelimits';
import type { FossaPlanName } from '../subscriptions';

/**
 * This function looks at what features the organization has enabled and returns the permissions required to view and
 * export the Issue Overview data. The user can access the overview if they can view at least one issue type on at least
 * one project, but we don't want to include disabled issue types.
 */
function getIssueOverviewResourceTypes(
  hasSecurity: boolean,
  hasQuality: boolean,
): Array<{ scoped: ResourceType; any: ResourceType }> {
  const possibleResources = [
    { scoped: ResourceType.ProjectLicensingIssue, any: ResourceType.ProjectLicensingIssueAny },
  ];

  if (hasSecurity) {
    possibleResources.push({
      scoped: ResourceType.ProjectSecurityIssue,
      any: ResourceType.ProjectSecurityIssueAny,
    });
  }

  if (hasQuality) {
    possibleResources.push({
      scoped: ResourceType.ProjectQualityIssue,
      any: ResourceType.ProjectQualityIssueAny,
    });
  }

  return possibleResources;
}

type OrgForAccessControl = {
  access_level: OrganizationAccessLevel;
  flags: string[];
  on_prem: boolean;
  stripe_plan?: FossaPlanName;
  stripePriceLimit?: StripePriceLimit;
};

export const isIssueOverviewEnabled = (org: OrgForAccessControl): boolean => {
  return org.on_prem || (org.access_level !== 'free' && !isFreeTier({
    plan: org.stripe_plan,
    stripePriceLimit: org.stripePriceLimit,
  }));
};

/**
 * Check whether the user has the permission to view the issue overview.  The user needs to have at access to at least
 * one issue type on at least one project.
 */
export const canAccessIssueOverview = (
  permissions: Permission[],
  hasSecurity: boolean,
  hasQuality: boolean,
): boolean => {
  return permissions.some((p) =>
    getIssueOverviewResourceTypes(hasSecurity, hasQuality)
      .flatMap(({ scoped, any }) => [scoped, any])
      .some((resourceType) => p.resourceType === resourceType && p.action === Action.View),
  );
};

/**
 * Check whether the user has the permission to view the issue overview for a specific project.  The user needs to have
 * access to at least one issue type on that project or globally.
 */
export const canAccessProjectIssueOverview = (
  permissions: Permission[],
  projectId: string,
  hasSecurity: boolean,
  hasQuality: boolean,
): boolean => {
  return permissions.some((p) =>
    getIssueOverviewResourceTypes(hasSecurity, hasQuality).some(
      ({ scoped: scopedResourceType, any: anyResourceType }) =>
        (p.resourceType === anyResourceType || _.isMatch(p, { resourceType: scopedResourceType, projectId })) &&
        p.action === Action.View,
    ),
  );
};
